'use client';
import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,  } from 'recharts';
import { useAuth } from '@/contexts/AuthContext';
import { analyticsService } from '@/lib/services/analyticsService';

interface PerformanceTrend {
  date: string;
  score: number;
  tests: number;
}

interface CategoryMastery {
  name: string;
  value: number;
  color: string;
}

interface ScoreDistribution {
  range: string;
  count: number;
}

const StudentAnalyticsReports = () => {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year'>('month');
  const [performanceTrends, setPerformanceTrends] = useState<PerformanceTrend[]>([]);
  const [categoryMastery, setCategoryMastery] = useState<CategoryMastery[]>([]);
  const [scoreDistribution, setScoreDistribution] = useState<ScoreDistribution[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user]);

  const loadAnalytics = async () => {
    if (!user) return;

    setLoading(true);
    const analytics = await analyticsService.getUserAnalytics(user.id);

    const mastery: CategoryMastery[] = analytics.map((cat) => ({
      name: cat.categoryName,
      value: Math.round(cat.accuracy),
      color:
        cat.accuracy >= 90
          ? '#10b981'
          : cat.accuracy >= 80
          ? '#3b82f6'
          : cat.accuracy >= 70
          ? '#f59e0b'
          : '#ef4444',
    }));

    setCategoryMastery(mastery);
    setPerformanceTrends([]);
    setScoreDistribution([]);
    setLoading(false);
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Student Analytics Report - Generated on ' + new Date().toLocaleDateString()],
      [],
      ['Category Mastery'],
      ['Category', 'Accuracy'],
      ...categoryMastery.map((cat) => [cat.name, `${cat.value}%`]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <section className="bg-card rounded-2xl p-6 md:p-8 border border-border">
        <div className="text-center py-8 text-muted-foreground">Loading analytics...</div>
      </section>
    );
  }

  return (
    <section className="bg-card rounded-2xl p-6 md:p-8 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-1">Analytics Reports</h2>
          <p className="text-sm text-muted-foreground">Detailed performance insights and trends</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <Icon name="ArrowDownTrayIcon" size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Category Mastery Chart */}
      {categoryMastery.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-4">Category Mastery</h3>
          <div className="bg-background rounded-xl p-6">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryMastery}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryMastery?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {categoryMastery.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="ChartBarIcon" size={48} className="mx-auto mb-4 opacity-50" />
          <p>No analytics data yet</p>
          <p className="text-sm mt-2">Start answering questions to see your performance analytics</p>
        </div>
      )}
    </section>
  );
};

export default StudentAnalyticsReports;